"use client";

import React, { useState, useEffect } from "react";
import { Globe, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqItem {
  q_vi: string;
  q_en: string;
  a_vi: string;
  a_en: string;
}
const FAQ_DATA: FaqItem[] = [{
  q_vi: "Robotics tại ROBOTO dành cho độ tuổi nào?",
  q_en: "What ages does ROBOTO's Robotics pathway serve?",
  a_vi: "Lộ trình kéo dài từ 4 đến 13+ tuổi, được cấu trúc theo các giai đoạn: D1-D4 (Duplo) cho độ tuổi 4-6, HP1-HP6 (WhalesBot AI Module 1S) cho độ tuổi 7+, và giai đoạn chuẩn bị ENJOY AI sau HP6; mỗi giai đoạn có khoảng độ tuổi cụ thể riêng.",
  a_en: "The pathway spans ages 4 to 13+, structured in stages: D1-D4 (Duplo) for ages 4-6, HP1-HP6 (WhalesBot AI Module 1S) for ages 7+, and ENJOY AI preparation after HP6; each stage has its own specific age range."
}, {
  q_vi: "D1–D4 sử dụng thiết bị gì?",
  q_en: "What equipment does D1-D4 use?",
  a_vi: "Duplo (LEGO Education).",
  a_en: "Duplo (LEGO Education)."
}, {
  q_vi: "Duplo có cần máy tính không?",
  q_en: "Does Duplo require a computer?",
  a_vi: "Không, không yêu cầu máy tính.",
  a_en: "No, not required."
}, {
  q_vi: "Có thể sử dụng tablet trong lớp Duplo không?",
  q_en: "Can tablets be used in Duplo classes?",
  a_vi: "Máy tính bảng có thể được sử dụng như một thiết bị bổ trợ tùy chọn cho các hoạt động tương tác, không phải là yêu cầu bắt buộc.",
  a_en: "Tablets may be used as an optional supplementary device for interactive activities, not a requirement."
}, {
  q_vi: "HP1–HP6 sử dụng thiết bị gì?",
  q_en: "What equipment does HP1-HP6 use?",
  a_vi: "WhalesBot AI Module 1S.",
  a_en: "WhalesBot AI Module 1S."
}, {
  q_vi: "WhalesBot có cần máy tính không?",
  q_en: "Does WhalesBot require a computer?",
  a_vi: "Có, bắt buộc phải sử dụng máy tính.",
  a_en: "Yes, a computer is required."
}, {
  q_vi: "Một bộ kit dành cho bao nhiêu học sinh?",
  q_en: "How many students share one kit?",
  a_vi: "2 học sinh/bộ là mô hình lý tưởng; 3 học sinh/bộ là mô hình tối ưu chi phí.",
  a_en: "2 students per kit is the ideal model; 3 students per kit is the cost-optimized model."
}, {
  q_vi: "2 học sinh/bộ và 3 học sinh/bộ khác nhau như thế nào?",
  q_en: "What's the difference between the 2-student and 3-student kit models?",
  a_vi: "Mô hình 2 học sinh/bộ là mô hình lý tưởng cho tương tác thực hành tối đa; mô hình 3 học sinh/bộ là giải pháp thay thế tối ưu chi phí và không được mô tả là hoàn toàn giống hệt về trải nghiệm so với mô hình 2 học sinh.",
  a_en: "2 students/kit is the ideal model for maximum hands-on interaction; 3 students/kit is a cost-optimized alternative and is not presented as identical in experience to the 2-student model."
}, {
  q_vi: "Sau D4 học viên sẽ chuyển sang đâu?",
  q_en: "What comes after D4?",
  a_vi: "Học viên chuyển sang HP1 (dòng WhalesBot AI Module 1S), sau khi hoàn thành D4 hoặc thông qua bài đánh giá đầu vào dành cho học viên từ 7 tuổi trở lên.",
  a_en: "Students transition to HP1 (WhalesBot AI Module 1S track), either after completing D4 or via an entrance assessment for students aged 7+."
}, {
  q_vi: "Sau HP6 học viên có thể tiếp tục chương trình nào?",
  q_en: "What can students continue to after HP6?",
  a_vi: "Một giai đoạn chuẩn bị ENJOY AI riêng biệt (rõ ràng không gọi là HP7), không đảm bảo kết quả cuộc thi hay giải thưởng.",
  a_en: "A separate ENJOY AI preparation stage (explicitly not called HP7), with no guarantee of competition results or awards."
}];
export default function FaqAccordion() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const saved = localStorage.getItem("roboto_lang");
    if (saved === "vi" || saved === "en") {
      setLang(saved);
    }
    const handleLangChange = (e: Event) => {
      const customEv = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEv.detail && (customEv.detail.lang === "vi" || customEv.detail.lang === "en")) {
        setLang(customEv.detail.lang);
      } else {
        const current = localStorage.getItem("roboto_lang");
        if (current === "vi" || current === "en") {
          setLang(current);
        }
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  const changeLanguage = (newLang: "vi" | "en") => {
    setLang(newLang);
    localStorage.setItem("roboto_lang", newLang);
    window.dispatchEvent(new CustomEvent("roboto-lang-change", {
      detail: {
        lang: newLang
      }
    }));
  };
  return <section id="faqaccordion" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center space-y-4 mb-12">
          <Badge variant="secondary" className="gap-1.5 px-3 py-1 text-xs font-semibold">
            <HelpCircle className="size-3.5 text-primary" />
            {lang === "vi" ? "Giải đáp thắc mắc" : "FAQ"}
          </Badge>

          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-serif">
            {lang === "vi" ? "Câu Hỏi Thường Gặp" : "Frequently Asked Questions"}
          </h2>

          <p className="text-muted-foreground text-sm sm:text-base max-w-2xl leading-relaxed">
            {lang === "vi" ? "Tìm hiểu chi tiết thông tin chính xác về lộ trình đào tạo, thiết bị và mô hình lớp học tại ROBOTO." : "Find clear and accurate details regarding our Robotics pathways, equipment, and class models at ROBOTO."}
          </p>

          {/* Language Selector Toggle */}
          <div className="pt-2 flex items-center gap-1.5 p-1 bg-muted rounded-lg border border-border">
            <div className="flex items-center gap-1 px-2 text-xs font-medium text-muted-foreground">
              <Globe className="size-3.5" />
              <span>Language:</span>
            </div>
            <Button type="button" variant={lang === "vi" ? "default" : "ghost"} size="sm" className="h-7 text-xs px-3 font-semibold rounded-md transition-all" onClick={() => changeLanguage("vi")}>
              Tiếng Việt
            </Button>
            <Button type="button" variant={lang === "en" ? "default" : "ghost"} size="sm" className="h-7 text-xs px-3 font-semibold rounded-md transition-all" onClick={() => changeLanguage("en")}>
              English
            </Button>
          </div>
        </div>

        {/* Accordion Component */}
        <Accordion type="single" collapsible className="w-full space-y-3">
          {FAQ_DATA.map((faq, index) => {
          const itemNum = index + 1 < 10 ? `0${index + 1}` : `${index + 1}`;
          return <AccordionItem key={index} value={`faq-item-${index}`} data-index={index} className="border border-border rounded-xl bg-card px-5 py-1 shadow-2xs hover:border-primary/40 transition-colors duration-200">
                <AccordionTrigger className="text-base font-semibold py-4 hover:no-underline text-card-foreground hover:text-primary transition-colors text-left">
                  <span className="flex items-start sm:items-center gap-3 pr-2">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-bold mt-0.5 sm:mt-0">
                      {itemNum}
                    </span>
                    <span>{lang === "vi" ? faq.q_vi : faq.q_en}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4 pt-1 pl-9 border-t border-border/50 mt-1">
                  {lang === "vi" ? faq.a_vi : faq.a_en}
                </AccordionContent>
              </AccordionItem>;
        })}
        </Accordion>
      </div>
    </section>;
}